import { portalApi } from "./client";
import type {
  PortalLoginResponseDto,
  PortalMeDto,
  PortalSetPasswordTokenStatus,
  ValidateInvitationResult,
} from "./types";

// External Intake & Collection v1.0 PR 4 — magic-link redeem + session
// inspection. Tracks the four steps from PR 3: validate → otp/send →
// otp/verify → redeem.
//
// External Portal v2 Phase 1 — adds the set-password confirm endpoint
// for accounts that move from passwordless to password.
// External Portal v2 Phase 1b — login now returns a discriminated
// PortalLoginResponseDto, and the MFA enroll / verify endpoints are
// surfaced here.

export const portalAuthApi = {
  me: () => portalApi.get<PortalMeDto>("/auth/me"),
  logout: () => portalApi.post<void>("/auth/logout"),

  // §7 — acknowledge the one-time landing-tab welcome panel. Idempotent on
  // the server; the panel calls this when it mounts.
  markFirstLoginSeen: () => portalApi.post<void>("/auth/me/first-login-seen"),

  // UAT FixBatch FIND-16 — upload a broker profile picture (multipart, field
  // name `file`). Returns the new avatar URL.
  uploadPhoto: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return portalApi.upload<{ avatarUrl: string }>("/auth/photo", form);
  },

  // UAT FixBatch FIND-17 — acknowledge the one-time certification welcome
  // modal. Idempotent on the server.
  acknowledgeWelcome: () =>
    portalApi.post<void>("/broker/welcome-acknowledged"),

  // Broker self-apply follow-ups — acknowledge the one-time "Application
  // received" modal a self-applied broker sees on first login. Also stamps
  // FirstLoginAt server-side so the inline welcome panel stays suppressed.
  // Idempotent on the server.
  acknowledgeApplicationWelcome: () =>
    portalApi.post<void>("/auth/me/application-welcome-seen"),

  // Sender-Fields v1 — acknowledge the one-time "you've been pre-approved"
  // celebration for a specific pre-approval letter item. Idempotent server-side.
  acknowledgePreApprovalCelebration: (itemId: string) =>
    portalApi.post<void>(`/auth/me/preapproval-celebration-seen?itemId=${encodeURIComponent(itemId)}`),

  validate: (token: string) =>
    portalApi.post<ValidateInvitationResult>("/invitations/validate", { token }),
  sendOtp: (token: string) =>
    portalApi.post<void>("/invitations/otp/send", { token }),
  verifyOtp: (token: string, code: string) =>
    portalApi.post<void>("/invitations/otp/verify", { token, code }),
  redeem: (token: string) =>
    portalApi.post<PortalMeDto>("/invitations/redeem", { token }),

  // External Portal v2 Phase 1 — login is keyed on email + password for
  // every PortalUserKind (was broker-only). MFA was removed in Round-3;
  // valid credentials resolve straight to a signed-in session.
  login: (email: string, password: string, rememberMe = false) =>
    portalApi.post<PortalLoginResponseDto>("/auth/login", { email, password, rememberMe }),

  // Set-password (Phase 1) — invited Buyer / Seller / Vendor accounts
  // bind a password before they can log in.
  setPasswordConfirm: (token: string, newPassword: string) =>
    portalApi.post<void>("/auth/set-password/confirm", { token, newPassword }),

  // §3.2 — non-consuming check so the set-password page can redirect a
  // stale / used link to sign-in before rendering the form.
  validateSetPasswordToken: (token: string) =>
    portalApi.post<PortalSetPasswordTokenStatus>("/auth/set-password/validate", { token }),

  passwordResetRequest: (email: string) =>
    portalApi.post<void>("/auth/password-reset/request", { email }),
  passwordResetConfirm: (token: string, newPassword: string) =>
    portalApi.post<void>("/auth/password-reset/confirm", { token, newPassword }),

  // Admin/staff-initiated password reset — the signed-in user changes their own
  // password (forced-change gate after an admin temp password, or the Account &
  // settings "Change password" form). Clears MustChangePassword server-side.
  changePassword: (currentPassword: string, newPassword: string) =>
    portalApi.post<void>("/auth/change-password", { currentPassword, newPassword }),

  // Non-prod test seam. Returns 404 in production (the dev stash is wired
  // to the no-op implementation outside Development).
  devGetOtp: (invitationId: string) =>
    portalApi.get<string>(`/dev/invitations/${invitationId}/otp`),
  devGetToken: (invitationId: string) =>
    portalApi.get<string>(`/dev/invitations/${invitationId}/token`),
};
